<template>
  <v-sheet
    class="outer-container pb-3"
  >
    <div
      v-if="isNonEnglish"
      class="non-english-notice d-flex align-center pb-1 px-4 pt-6"
    >
      <LanguageBadge
        :language-code="languageCode"
        class="flex-shrink-0 mr-3"
      />
      <p class="mb-0 text-body-2 text-textLight">
        Scoring is available for English posts only
      </p>
    </div>
    <div
      v-else
      class="pb-1 px-4 pt-6"
    >
      <transition>
        <div
          v-if="showConfetti && !isTemplate"
          class="confetti"
        >
          <div
            ref="confetti-player"
            class="confetti-player"
          />
        </div>
      </transition>

      <ScoreAndScales
        class="mb-4"
        :job-id="jobId"
        :score="score"
        :word-count="wordCount"
        :scales="scales"
        :specificity-key="specificityKey"
        :specificity-data="specificityData"
        :inclusion-meter-disabled="inclusionMeterDisabled"
        @track-sidebar-event="$emit('track-sidebar-event', $event)"
      >
        <template #scales-block>
          <ScalesBlock
            :scales="scales"
            class="mt-2"
          />
        </template>
      </ScoreAndScales>

      <p
        class="mb-4 text-h2"
      >
        <template v-if="documentType === 'email'">
          <template v-if="score >= 85">
            Email is ready <br>to send
          </template>
          <template v-else-if="score >= 70">
            Fix style and language errors
          </template>
          <template v-else-if="score >= 50">
            Email is difficult to understand
          </template>
          <template v-else-if="score >= 0">
            Email is difficult to understand
          </template>
          <template v-else>
            Critical errors must <br>be fixed
          </template>
        </template>

        <template v-else-if="documentType === 'job_template'">
          <template v-if="score >= 85">
            Your template is <br>ready to use
          </template>
          <template v-else-if="score >= 70">
            Your template has several issues
          </template>
          <template v-else-if="score >= 50">
            Your template has several critical issues
          </template>
          <template v-else-if="score >= 0">
            Your template has several critical issues
          </template>
          <template v-else>
            Fix critical issues to begin scoring
          </template>
        </template>
      </p>

      <hr
        v-if="showRanking"
        class="mb-3"
        style="border-top: #DDDFE3 solid 1px"
      >
    </div>

    <Ranking
      v-if="showRanking && !isNonEnglish"
      :score-class="scoreClass"
      :percentage="ranking.percentage"
      :tags="ranking.tags"
      :location="ranking.location"
    />

    <GuidanceList
      v-if="!isNonEnglish"
      class="pl-4"
      :body-messages="bodyMessages"
      :description-json="{ fields }"
      :post-summary="postSummary"
      :is-admin="isAdmin"
      :is-revision-saving="isRevisionSaving"
      :open-category="openCategory"
      :document-type="documentType"
      @add="handleMessageAdd"
      @report-link-click="onReportLinkClick"
      @track-sidebar-event="$emit('track-sidebar-event', $event)"
      @show-apply-template="onShowApplyTemplate"
    />
  </v-sheet>
</template>

<script>
/* eslint-disable vue/require-prop-types */
import Ranking from '@/components/editor/Ranking.vue';
import { mapState } from 'pinia';
import { nextTick } from 'vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppRoute } from '@/stores/app/route';
import { useApplyTemplate } from '@/stores/document/applyTemplate';
import { useDatapeopleAnywhereEditor } from '@/stores/document/datapeopleAnywhereEditor';
import { useEditor } from '@/stores/editor/editor';
import { useTracking } from '@/stores/tracking/tracking';
import ScoreAndScales from '@/components/document/sideBar/ScoreAndScales.vue';
import GuidanceList from '@ui/components/editor/GuidanceList/GuidanceList.vue';
import ScalesBlock from '@/components/editor/ScalesBlock.vue';
import LanguageBadge from '@/components/shared/LanguageBadge.vue';

export default {
  name: 'SideBar',
  components: {
    ScoreAndScales,
    GuidanceList,
    Ranking,
    ScalesBlock,
    LanguageBadge,
  },
  props: [
    'isDataLoaded',
    'bodyMessages',
    'hasValidScore',
    'ranking',
    'sidebarMessages',
    'scales',
    'savedStatus',
    'view',
    'focus',
    'nudges',
    'isRevisionSaving',
    'inclusionMeterDisabled',
  ],
  emits: ['oneClickAdd', 'track-sidebar-event'],
  data() {
    return {
      hover: false,
      showConfetti: false,
    };
  },
  computed: {
    ...mapState(useAppAuthData, ['isAdmin']),
    ...mapState(useAppRoute, ['previousRoute', 'isChromeView']),
    ...mapState(useDatapeopleAnywhereEditor, ['isDatapeopleAnywhereEditor']),
    ...mapState(useEditor, [
      'documentType', 'score', 'isTemplate', 'isEditable', 'jobId',
      'specificityKey', 'specificityData', 'wordCount', 'postSummary', 'fields',
      'languageCode', 'isNonEnglish',
    ]),
    openCategory() {
      if (['compliance', 'brand', 'performance'].includes(this.$route.query?.category)) return this.$route.query.category;

      return {
        'compliance-report': 'compliance',
        'employer-brand-report': 'brand',
        'job-performance-report': 'performance',
      }[this.previousRoute?.name] || '';
    },
    isMechanicsPresent() {
      return typeof this.scales.mechanics?.value === 'number';
    },
    showRanking() {
      const { percentage, score } = this.ranking;
      return percentage !== null && percentage > 40 && score > 70;
    },
    scoreClass() {
      if (this.score === null) {
        return 'score-none';
      } if (this.score >= 85) {
        return 'score-good';
      } if (this.score >= 70) {
        return 'score-near-good';
      } if (this.score >= 50) {
        return 'score-poor';
      }
      return 'score-bad';
    },
  },
  watch: {
    score: {
      immediate: true,
      handler(newValue, oldValue) {
        if (!oldValue || !newValue) return;
        if (oldValue < 85 && newValue >= 85) {
          this.showConfetti = true;
          this.toggleTransition();
          nextTick(async () => {
            const { default: confetti } = await import('@/assets/lottieFiles/score-confetti');
            const { default: lottie } = await import('lottie-web/build/player/lottie_light');

            lottie.loadAnimation({
              container: this.$refs['confetti-player'],
              renderer: 'svg',
              autoplay: true,
              animationData: confetti,
            });
          });
        }
      },
    },
  },
  methods: {
    toggleTransition() {
      setTimeout(() => {
        this.showConfetti = false;
      }, 1200);
    },
    handleMessageAdd(hash) {
      if (!this.isEditable) {
        return;
      }

      const messageToAdd = this.bodyMessages[hash];
      const allowedForOneClickAdd = ['red-solid', 'blue-solid', 'yellow-solid'];

      if (!allowedForOneClickAdd.includes(messageToAdd?.severity_class)) {
        return;
      }

      if (messageToAdd?.suggestions.length) {
        // Always just auto-add the first suggestion
        this.$emit('oneClickAdd', messageToAdd.suggestions[0]);
      }
    },
    onShowApplyTemplate() {
      if (!this.isEditable) {
        return;
      }

      useApplyTemplate().setModalVisible(true);
    },

    onReportLinkClick({ panelId, documentType, isAdmin }) {
      const reportNameByPanelId = {
        performance: 'job-performance-report',
        brand: 'employer-brand-report',
        compliance: 'compliance-report',
      };

      const reportName = reportNameByPanelId[panelId];

      if (reportName) {
        useTracking().editorActionableInsightsReportLinkClick({ reportName });
      }

      let to = null;
      if (panelId === 'performance') {
        to = {
          name: 'job-performance-report',
          query: {
            owned: !isAdmin || undefined,
            internal: 'external',
            page: 1,
          },
        };
      } else if (panelId === 'brand') {
        to = {
          name: 'employer-brand-report',
          query: {
            owned: !isAdmin || undefined,
            templates: documentType === 'job_template' || undefined,
            internal: 'external',
            page: 1,
          },
        };
      } else if (panelId === 'compliance') {
        to = {
          name: 'compliance-report',
          query: {
            owned: !isAdmin || undefined,
            internal: 'external',
            page: 1,
          },
        };
      }

      if (to) {
        if (this.isChromeView) {
          const resolved = this.$router.resolve(to) || {};
          const { href } = resolved;
          if (href) {
            window.open(href, '_blank');
          }
        } else {
          this.$router.push(to);
        }
      }
    },
  },
};
</script>

<style scoped lang="less">
.outer-container {
  position: sticky;
  top: 0;
  align-self: flex-start;
  z-index: 3;
  overflow-y: auto;
  overflow-x: hidden;
}
.non-english-notice p {
  color: @grey-800;
}

.confetti {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 4;
}

.confetti-player {
  width: 284px;
  height: 180px;
  background-color: @white;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
